import { WorldPoint, RoadSegment } from '../domain/types.ts';
import { createNeighborhoodMap } from '../map/mapData.ts';

/**
 * Node trong đồ thị mạng lưới đường giao thông
 */
export interface GraphNode {
  id: string;
  x: number;
  y: number;
  neighbors: Map<string, number>; // targetNodeId -> khoảng cách (mét)
}

/**
 * Cấu trúc đồ thị mạng lưới đường (Road Network Graph)
 */
export interface RoadGraph {
  nodes: Map<string, GraphNode>;
  subSegments: {
    aId: string;
    bId: string;
    a: WorldPoint;
    b: WorldPoint;
    roadId: string;
  }[];
}

/**
 * Tính khoảng cách từ một điểm P tới đoạn thẳng AB và điểm chiếu vuông góc
 */
export function distToSegment(
  p: WorldPoint,
  a: WorldPoint,
  b: WorldPoint
): { dist: number; closest: WorldPoint; t: number } {
  const l2 = (b.x - a.x) * (b.x - a.x) + (b.y - a.y) * (b.y - a.y);
  if (l2 === 0) {
    return { dist: Math.hypot(p.x - a.x, p.y - a.y), closest: { x: a.x, y: a.y }, t: 0 };
  }
  let t = ((p.x - a.x) * (b.x - a.x) + (p.y - a.y) * (b.y - a.y)) / l2;
  t = Math.max(0, Math.min(1, t));
  const closest = {
    x: a.x + t * (b.x - a.x),
    y: a.y + t * (b.y - a.y),
  };
  return { dist: Math.hypot(p.x - closest.x, p.y - closest.y), closest, t };
}

/**
 * Tính giao điểm giữa 2 đoạn thẳng AB và CD (cắt chéo nhau)
 */
export function segmentIntersection(
  a: WorldPoint,
  b: WorldPoint,
  c: WorldPoint,
  d: WorldPoint
): { x: number; y: number; t: number; u: number } | null {
  const dx1 = b.x - a.x;
  const dy1 = b.y - a.y;
  const dx2 = d.x - c.x;
  const dy2 = d.y - c.y;

  const denom = dx1 * dy2 - dy1 * dx2;
  if (Math.abs(denom) < 1e-6) return null; // Song song hoặc trùng hàng

  const t = ((c.x - a.x) * dy2 - (c.y - a.y) * dx2) / denom;
  const u = ((c.x - a.x) * dy1 - (c.y - a.y) * dx1) / denom;

  const eps = 1e-4;
  if (t >= eps && t <= 1 - eps && u >= eps && u <= 1 - eps) {
    return {
      x: a.x + t * dx1,
      y: a.y + t * dy1,
      t,
      u,
    };
  }
  return null;
}

// Bộ nhớ đệm đồ thị theo mảng roads
let cachedRoadsRef: RoadSegment[] | null = null;
let cachedGraph: RoadGraph | null = null;

/**
 * Xây dựng đồ thị mạng lưới đường giao thông (Road Graph)
 * Hỗ trợ triệt để:
 * 1. Giao điểm cắt chéo X
 * 2. Ngã ba chữ T (T-junctions) - điểm mút đường này đâm vào giữa đoạn đường kia
 * 3. Tự động hàn gắn (bridge) các cụm đường rời rạc trong dữ liệu OSM
 */
export function buildRoadGraph(roads: RoadSegment[]): RoadGraph {
  if (cachedRoadsRef === roads && cachedGraph) {
    return cachedGraph;
  }

  const nodes = new Map<string, GraphNode>();
  let nextNodeId = 1;

  function getOrCreateNode(pt: WorldPoint): string {
    // Gộp các đỉnh gần nhau (< 1.0m) vào làm một ngã rẽ/node chung
    for (const [id, node] of nodes.entries()) {
      if (Math.hypot(node.x - pt.x, node.y - pt.y) < 1.0) {
        return id;
      }
    }
    const id = `n_${nextNodeId++}`;
    nodes.set(id, { id, x: pt.x, y: pt.y, neighbors: new Map() });
    return id;
  }

  // 1. Thu thập tất cả các đoạn thẳng gốc từ các con đường
  interface RawSeg {
    roadId: string;
    a: WorldPoint;
    b: WorldPoint;
    splits: { pt: WorldPoint; t: number }[];
  }
  const rawSegments: RawSeg[] = [];

  for (const road of roads) {
    for (let i = 0; i < road.points.length - 1; i++) {
      rawSegments.push({
        roadId: road.id,
        a: road.points[i]!,
        b: road.points[i + 1]!,
        splits: [],
      });
    }
  }

  // 2. Giao điểm cắt chéo nhau (X-intersections)
  for (let i = 0; i < rawSegments.length; i++) {
    for (let j = i + 1; j < rawSegments.length; j++) {
      const seg1 = rawSegments[i]!;
      const seg2 = rawSegments[j]!;
      const hit = segmentIntersection(seg1.a, seg1.b, seg2.a, seg2.b);
      if (hit) {
        const pt = { x: hit.x, y: hit.y };
        seg1.splits.push({ pt, t: hit.t });
        seg2.splits.push({ pt, t: hit.u });
      }
    }
  }

  // 3. Xử lý ngã ba chữ T (T-Junctions): Điểm mút của đường này nối/chạm vào lòng đường kia (khoảng cách <= 4.0m)
  const T_JUNCTION_MAX_DIST = 4.0;
  for (let i = 0; i < rawSegments.length; i++) {
    const seg1 = rawSegments[i]!;
    for (let j = 0; j < rawSegments.length; j++) {
      if (i === j) continue;
      const seg2 = rawSegments[j]!;

      // Kiểm tra mút A của seg1 chiếu lên seg2
      const resA = distToSegment(seg1.a, seg2.a, seg2.b);
      if (resA.dist <= T_JUNCTION_MAX_DIST && resA.t > 0.01 && resA.t < 0.99) {
        seg2.splits.push({ pt: resA.closest, t: resA.t });
      }

      // Kiểm tra mút B của seg1 chiếu lên seg2
      const resB = distToSegment(seg1.b, seg2.a, seg2.b);
      if (resB.dist <= T_JUNCTION_MAX_DIST && resB.t > 0.01 && resB.t < 0.99) {
        seg2.splits.push({ pt: resB.closest, t: resB.t });
      }
    }
  }

  // 4. Sắp xếp các điểm chia tách dọc theo từng đoạn đường và tạo sub-segments
  const subSegments: RoadGraph['subSegments'] = [];

  function addEdge(id1: string, id2: string, dist: number) {
    if (id1 === id2) return;
    const n1 = nodes.get(id1)!;
    const n2 = nodes.get(id2)!;
    n1.neighbors.set(id2, dist);
    n2.neighbors.set(id1, dist);
  }

  for (const seg of rawSegments) {
    seg.splits.sort((a, b) => a.t - b.t);
    const chain: WorldPoint[] = [seg.a, ...seg.splits.map((s) => s.pt), seg.b];

    for (let k = 0; k < chain.length - 1; k++) {
      const p1 = chain[k]!;
      const p2 = chain[k + 1]!;
      const d = Math.hypot(p2.x - p1.x, p2.y - p1.y);
      if (d > 0.05) {
        const id1 = getOrCreateNode(p1);
        const id2 = getOrCreateNode(p2);
        if (id1 !== id2) {
          addEdge(id1, id2, d);
          subSegments.push({
            aId: id1,
            bId: id2,
            a: nodes.get(id1)!,
            b: nodes.get(id2)!,
            roadId: seg.roadId,
          });
        }
      }
    }
  }

  // 5. Đảm bảo Đồ thị liên thông 100% (Component Bridging)
  // Trong OSM, đôi khi các nhánh ngõ nhỏ bị ngắt quãng vài mét.
  // Ta nối các thành phần liên thông rời rạc bằng cạnh ngắn nhất giữa chúng.
  const visited = new Set<string>();
  const components: string[][] = [];

  for (const nodeId of nodes.keys()) {
    if (!visited.has(nodeId)) {
      const comp: string[] = [];
      const queue = [nodeId];
      visited.add(nodeId);
      while (queue.length > 0) {
        const curr = queue.shift()!;
        comp.push(curr);
        const node = nodes.get(curr);
        if (node) {
          for (const neighborId of node.neighbors.keys()) {
            if (!visited.has(neighborId)) {
              visited.add(neighborId);
              queue.push(neighborId);
            }
          }
        }
      }
      components.push(comp);
    }
  }

  if (components.length > 1) {
    components.sort((a, b) => b.length - a.length);
    const mainComp = components[0]!;

    for (let c = 1; c < components.length; c++) {
      const otherComp = components[c]!;
      let bestDist = Infinity;
      let bestMainId = mainComp[0]!;
      let bestOtherId = otherComp[0]!;

      for (const mId of mainComp) {
        const mNode = nodes.get(mId)!;
        for (const oId of otherComp) {
          const oNode = nodes.get(oId)!;
          const d = Math.hypot(mNode.x - oNode.x, mNode.y - oNode.y);
          if (d < bestDist) {
            bestDist = d;
            bestMainId = mId;
            bestOtherId = oId;
          }
        }
      }

      addEdge(bestMainId, bestOtherId, bestDist);
      subSegments.push({
        aId: bestMainId,
        bId: bestOtherId,
        a: nodes.get(bestMainId)!,
        b: nodes.get(bestOtherId)!,
        roadId: 'bridge',
      });
      mainComp.push(...otherComp);
    }
  }

  const result: RoadGraph = { nodes, subSegments };
  cachedRoadsRef = roads;
  cachedGraph = result;
  return result;
}

/**
 * Snap một điểm bất kỳ trên màn hình về điểm gần nhất trên lòng đường
 */
export function snapToRoad(
  point: WorldPoint,
  activeRoads?: RoadSegment[]
): { snapped: WorldPoint; roadId: string; dist: number } {
  let closestDist = Infinity;
  let bestPoint: WorldPoint = { x: point.x, y: point.y };
  let bestRoadId = 'road-0';

  const roads =
    activeRoads && activeRoads.length > 0 ? activeRoads : createNeighborhoodMap().roads;

  for (const road of roads) {
    for (let i = 0; i < road.points.length - 1; i++) {
      const a = road.points[i]!;
      const b = road.points[i + 1]!;
      const res = distToSegment(point, a, b);
      if (res.dist < closestDist) {
        closestDist = res.dist;
        bestPoint = res.closest;
        bestRoadId = road.id;
      }
    }
  }
  return { snapped: bestPoint, roadId: bestRoadId, dist: closestDist };
}

/**
 * Thuật toán tìm đường A* (A-Star) bám 100% trên mạng lưới đường giao thông
 * Tuyệt đối không bao giờ đi xuyên nhà hay cắt ngang đất trống
 */
export function findRoadPath(
  startPos: WorldPoint,
  targetPos: WorldPoint,
  activeRoads?: RoadSegment[]
): WorldPoint[] {
  const roads =
    activeRoads && activeRoads.length > 0 ? activeRoads : createNeighborhoodMap().roads;
  const graph = buildRoadGraph(roads);

  if (graph.subSegments.length === 0 || graph.nodes.size === 0) {
    const snap = snapToRoad(targetPos, roads);
    return [snap.snapped];
  }

  // Snap cả Start và Target vào lòng đường gần nhất
  const startSnap = snapToRoad(startPos, roads);
  const targetSnap = snapToRoad(targetPos, roads);

  const sPt = startSnap.snapped;
  const tPt = targetSnap.snapped;

  // Nếu điểm đến quá gần vị trí xuất phát (< 0.4m)
  if (Math.hypot(sPt.x - tPt.x, sPt.y - tPt.y) < 0.4) {
    return [tPt];
  }

  // Tìm đoạn subSegment gần nhất cho Start và Target
  function findNearestSubSegment(pt: WorldPoint) {
    let bestDist = Infinity;
    let bestSub = graph.subSegments[0]!;
    let bestClosest = pt;
    for (const sub of graph.subSegments) {
      const res = distToSegment(pt, sub.a, sub.b);
      if (res.dist < bestDist) {
        bestDist = res.dist;
        bestSub = sub;
        bestClosest = res.closest;
      }
    }
    return { sub: bestSub, pt: bestClosest };
  }

  const sSub = findNearestSubSegment(sPt);
  const tSub = findNearestSubSegment(tPt);

  // Nếu cả 2 cùng nằm trên một đoạn thẳng liền mạch
  if (sSub.sub === tSub.sub) {
    return [tPt];
  }

  // Bản sao các node đồ thị để gắn tạm thời Start và Target
  const nodes = new Map<string, GraphNode>();
  for (const [k, v] of graph.nodes.entries()) {
    nodes.set(k, {
      id: v.id,
      x: v.x,
      y: v.y,
      neighbors: new Map(v.neighbors),
    });
  }

  const startNodeId = 'TEMP_START';
  const targetNodeId = 'TEMP_TARGET';

  nodes.set(startNodeId, { id: startNodeId, x: sPt.x, y: sPt.y, neighbors: new Map() });
  nodes.set(targetNodeId, { id: targetNodeId, x: tPt.x, y: tPt.y, neighbors: new Map() });

  // Nối startNode với 2 đầu của đoạn đường sSub
  const distSA = Math.hypot(sPt.x - sSub.sub.a.x, sPt.y - sSub.sub.a.y);
  const distSB = Math.hypot(sPt.x - sSub.sub.b.x, sPt.y - sSub.sub.b.y);
  nodes.get(startNodeId)!.neighbors.set(sSub.sub.aId, Math.max(0.1, distSA));
  nodes.get(sSub.sub.aId)?.neighbors.set(startNodeId, Math.max(0.1, distSA));
  nodes.get(startNodeId)!.neighbors.set(sSub.sub.bId, Math.max(0.1, distSB));
  nodes.get(sSub.sub.bId)?.neighbors.set(startNodeId, Math.max(0.1, distSB));

  // Nối targetNode với 2 đầu của đoạn đường tSub
  const distTA = Math.hypot(tPt.x - tSub.sub.a.x, tPt.y - tSub.sub.a.y);
  const distTB = Math.hypot(tPt.x - tSub.sub.b.x, tPt.y - tSub.sub.b.y);
  nodes.get(targetNodeId)!.neighbors.set(tSub.sub.aId, Math.max(0.1, distTA));
  nodes.get(tSub.sub.aId)?.neighbors.set(targetNodeId, Math.max(0.1, distTA));
  nodes.get(targetNodeId)!.neighbors.set(tSub.sub.bId, Math.max(0.1, distTB));
  nodes.get(tSub.sub.bId)?.neighbors.set(targetNodeId, Math.max(0.1, distTB));

  // Khởi động A* (A-Star Algorithm)
  const gScore = new Map<string, number>();
  const fScore = new Map<string, number>();
  const cameFrom = new Map<string, string>();
  const openSet = new Set<string>([startNodeId]);

  gScore.set(startNodeId, 0);
  fScore.set(startNodeId, Math.hypot(sPt.x - tPt.x, sPt.y - tPt.y));

  let found = false;

  while (openSet.size > 0) {
    let currentId = '';
    let lowestF = Infinity;
    for (const id of openSet) {
      const f = fScore.get(id) ?? Infinity;
      if (f < lowestF) {
        lowestF = f;
        currentId = id;
      }
    }

    if (currentId === targetNodeId) {
      found = true;
      break;
    }

    openSet.delete(currentId);
    const currNode = nodes.get(currentId);
    if (!currNode) continue;
    const currentG = gScore.get(currentId) ?? Infinity;

    for (const [neighborId, weight] of currNode.neighbors.entries()) {
      const tentativeG = currentG + weight;
      if (tentativeG < (gScore.get(neighborId) ?? Infinity)) {
        cameFrom.set(neighborId, currentId);
        gScore.set(neighborId, tentativeG);
        const neighborNode = nodes.get(neighborId);
        if (neighborNode) {
          const h = Math.hypot(neighborNode.x - tPt.x, neighborNode.y - tPt.y);
          fScore.set(neighborId, tentativeG + h);
          openSet.add(neighborId);
        }
      }
    }
  }

  // Nếu không tìm thấy đường trực tiếp (vẫn đi dọc theo đồ thị tới điểm gần nhất, TUYỆT ĐỐI KHÔNG CẮT CHÉO ĐẤT TRỐNG)
  if (!found) {
    let bestReachableId = '';
    let minD = Infinity;
    for (const [nId] of gScore.entries()) {
      if (nId !== startNodeId && nId !== targetNodeId) {
        const node = nodes.get(nId);
        if (node) {
          const d = Math.hypot(node.x - tPt.x, node.y - tPt.y);
          if (d < minD) {
            minD = d;
            bestReachableId = nId;
          }
        }
      }
    }
    if (bestReachableId) {
      const fallbackNodes: string[] = [bestReachableId];
      let curr = bestReachableId;
      while (cameFrom.has(curr)) {
        curr = cameFrom.get(curr)!;
        fallbackNodes.unshift(curr);
      }
      const pts: WorldPoint[] = [];
      for (let i = 1; i < fallbackNodes.length; i++) {
        const n = nodes.get(fallbackNodes[i]!)!;
        pts.push({ x: n.x, y: n.y });
      }
      return pts.length > 0 ? pts : [sPt];
    }
    return [sPt];
  }

  // Tái hiện đường đi từ Target ngược về Start
  const pathNodes: string[] = [targetNodeId];
  let curr = targetNodeId;
  while (cameFrom.has(curr)) {
    curr = cameFrom.get(curr)!;
    pathNodes.unshift(curr);
  }

  const resultPoints: WorldPoint[] = [];
  for (let i = 1; i < pathNodes.length; i++) {
    const n = nodes.get(pathNodes[i]!)!;
    resultPoints.push({ x: n.x, y: n.y });
  }

  return resultPoints.length > 0 ? resultPoints : [tPt];
}

/**
 * Tương thích ngược: Alias cho findRoadPath
 */
export const findOrthogonalRoadPath = findRoadPath;
